import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMG1lZXRpbmd8ZW58MHx8fHwxNzgyMzg0MTcxfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");

  if (user) return <Navigate to="/app" replace />;

  const onLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) { toast.success("Welcome back!"); nav("/app"); }
    else toast.error(r.error);
  };

  const onRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await register(name, email, password);
    setLoading(false);
    if (r.ok) { toast.success("Account created!"); nav("/onboarding"); }
    else toast.error(r.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left hero */}
      <div className="relative hidden lg:block">
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/60 via-foreground/60 to-foreground/90" />
        <div className="relative h-full flex flex-col p-12 text-white">
          <div className="flex items-center gap-2 font-heading font-bold text-xl">
            <div className="w-10 h-10 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center">BP</div>
            TheBillPro
          </div>
          <div className="mt-auto max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-xs uppercase tracking-widest mb-6">
              <Sparkles size={14} /> Business OS for India
            </div>
            <h1 className="font-heading text-4xl xl:text-5xl font-bold tracking-tight leading-tight">
              Simple billing. Powerful business management.
            </h1>
            <p className="mt-4 text-white/80 leading-relaxed">
              GST invoices, inventory, payments, CRM, projects, and AI insights — built for
              interior designers, retailers, contractors and growing businesses.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 text-sm">
              {["GST Ready", "AI Assistant", "Multi-business"].map((t) => (
                <div key={t} className="px-3 py-2 rounded-lg bg-white/10 backdrop-blur text-center">{t}</div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <Card className="w-full max-w-md p-8 shadow-sm border-border/60">
          <div className="font-heading font-bold text-2xl">Welcome to TheBillPro</div>
          <p className="text-sm text-muted-foreground mt-1">Sign in to manage your business operations.</p>

          <Tabs defaultValue="login" className="mt-6">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="login" data-testid="tab-login">Sign in</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Create account</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6">
              <form onSubmit={onLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" required />
                </div>
                <div>
                  <Label htmlFor="pw">Password</Label>
                  <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" required />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
                  {loading ? <Loader2 className="animate-spin" size={16} /> : "Sign in"}
                </Button>
                <div className="text-xs text-center">
                  <a
                    href="mailto:support@thebillpro.com?subject=Password%20reset%20request&body=Hi%20TheBillPro%20team%2C%0A%0AI%20need%20to%20reset%20my%20password%20for%20the%20account%3A%0A%0AEmail%3A%20%0A%0AThanks."
                    className="text-primary hover:underline font-medium"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </a>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-6">
              <form onSubmit={onRegister} className="space-y-4">
                <div>
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" required />
                </div>
                <div>
                  <Label htmlFor="remail">Email</Label>
                  <Input id="remail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email" required />
                </div>
                <div>
                  <Label htmlFor="rpw">Password</Label>
                  <Input id="rpw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" required minLength={6} />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit">
                  {loading ? <Loader2 className="animate-spin" size={16} /> : "Create account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}
